// ./src/components/features/Notifications/NotificationBell.tsx
//
// The bell (ROADMAP A2b #57).
//
// ⚠⚠ THIS PRODUCT ALREADY HAD A BELL AND IT WAS NOT OURS. It carried an unread dot fed by
// `userNotificationList { carId … car { id label } }` — a query defined only in **carexpenses-api**
// — refreshed every 60 seconds for every signed-in user, and it opened a "Coming soon" page.
// Nothing ever failed loudly, because a query the gateway REJECTS looks exactly like an empty
// inbox. `npm run check-nav` asserts that shape stays gone.
//
// ⚠ SO THIS ONE: reads a count the provider already has (no fetch of its own, no poll), and opens a
// page that exists.
import React from "react";
import { useNavigate } from "react-router-dom";
import { ActionIcon, Indicator, Tooltip, useMantineTheme } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic/AppIcon";
import { useOptionalEmNotificationsContext } from "@/contexts/EmNotificationsContext";
import { getRoutePath } from "@/config/siteMap";

export interface NotificationBellProps {
  /** Matches the other header ActionIcons (40 on the phone bar). */
  size?: number;
  iconSize?: number;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({ size = 40, iconSize = 26 }) => {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const notifications = useOptionalEmNotificationsContext();

  // ⚠ NULL-TOLERANT, NOT AN ERROR. A crew session signed in with an event password has no account
  // user and therefore no bell; the shell must render perfectly without one rather than throwing on
  // a surface that is otherwise working.
  if (!notifications) {
    return null;
  }

  const unread = notifications.unread ?? 0;
  const label = t("notifications:title", { defaultValue: "Notifications" });

  return (
    <Tooltip label={label} position="bottom" withArrow openDelay={500}>
      <Indicator
        // ⚠ CAPPED AT 99+. An uncapped count widens the badge until it pushes the header around, and
        // the difference between 214 and 215 is not information anybody acts on.
        label={unread > 99 ? "99+" : unread || undefined}
        disabled={unread === 0}
        color="red"
        size={16}
        offset={4}
      >
        <ActionIcon
          variant="default"
          size={size}
          radius="md"
          onClick={() => navigate(getRoutePath("notifications"))}
          aria-label={
            unread > 0
              ? t("notifications:bellWithCount", { count: unread, defaultValue: `${label} (${unread} unread)` })
              : label
          }
          style={{ border: `1px solid ${theme.colors.neutral[2]}` }}
        >
          <AppIcon iconName="Bell" size={iconSize} />
        </ActionIcon>
      </Indicator>
    </Tooltip>
  );
};
